import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig"; // <-- adjust if your path differs

// Firestore "in" query has a max (commonly 30). Chunk to 25 for safety.
const IN_CHUNK = 25;

function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function stripSO(docNo) {
  if (!docNo) return "";
  return String(docNo).replace(/^SO/i, "").trim();
}

function normaliseCode(code) {
  // Keep EXACT productCode as stored in cyl_info (case-sensitive matching in Firestore)
  return String(code || "").trim();
}

function docIdForProductCode(code) {
  // Firestore-safe document id for ANY product code
  return encodeURIComponent(normaliseCode(code));
}

function decodeDocIdToProductCode(docId) {
  try {
    return decodeURIComponent(String(docId || ""));
  } catch {
    return String(docId || "");
  }
}

function formatTs(ts) {
  try {
    const d = ts?.toDate?.();
    if (d instanceof Date) return d.toLocaleString();
  } catch {}
  return "-";
}

function pillClass(kind) {
  switch (kind) {
    case "ok":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "warn":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "bad":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

function Pill({ kind, children }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        pillClass(kind),
      ].join(" ")}
    >
      {children}
    </span>
  );
}

  // ✅ Define what "on hand in warehouse" means (change this if needed)
  function isPhysicallyInWarehouse(doc) {
    return doc?.Stock === true && doc?.Dispatch === false;
  }

  function isAvailableWarehouse(doc) {
    return (
      doc?.Stock === true &&
      doc?.Dispatch === false &&
      doc?.Allocated !== true
    );
  }

  function isAllocatedWarehouse(doc) {
    return (
      doc?.Stock === true &&
      doc?.Dispatch === false &&
      doc?.Allocated === true
    );
  }

export default function StockMinLevels() {
  const [error, setError] = useState("");

  // stock_sales_live (NAV live for STOCK account)
  const [stockRows, setStockRows] = useState([]);
  const [loadingStock, setLoadingStock] = useState(true);

  const [cylUpperToReal, setCylUpperToReal] = useState(new Map());

  // user-defined targets
  const [targets, setTargets] = useState([]); // [{productCode, targetOnHand}]
  const [loadingTargets, setLoadingTargets] = useState(true);

  const [activeCode, setActiveCode] = useState(null); // product code clicked
  const [activeTargetInput, setActiveTargetInput] = useState("");

  // data needed to join
  const [loadingJoin, setLoadingJoin] = useState(false);
  const [cylInfoByProductCode, setCylInfoByProductCode] = useState(new Map()); // code -> {docId,...}
  const [warehouseDocsByProductId, setWarehouseDocsByProductId] = useState(new Map()); // productId -> [warehouse docs]
  const [warehouseExactByKey, setWarehouseExactByKey] = useState(new Map()); // `${productId}__${serial}` -> warehouse doc

  const [allCylProductCodes, setAllCylProductCodes] = useState([]);
  const [loadingCylList, setLoadingCylList] = useState(false);

  // UI
  const [search, setSearch] = useState("");
  const [showNeedsOrderOnly, setShowNeedsOrderOnly] = useState(false);

  // quick add/edit
  const [newCode, setNewCode] = useState("");
  const [newTarget, setNewTarget] = useState("5");
  const [saving, setSaving] = useState(false);

  // 1) Live subscribe to stock_sales_live
  useEffect(() => {
    setError("");
    setLoadingStock(true);

    const q = query(
      collection(db, "stock_sales_live"),
      orderBy("LastNavSyncUtc", "desc"),
      limit(2000) // safe-ish. adjust if needed.
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setStockRows(rows);
        setLoadingStock(false);
      },
      (err) => {
        setError(err?.message || String(err));
        setLoadingStock(false);
      }
    );

    return () => unsub();
  }, []);

  // 2) Live subscribe to targets collection
  useEffect(() => {
    setError("");
    setLoadingTargets(true);

    const q = query(collection(db, "stock_min_levels"), orderBy("productCode", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            productCode: normaliseCode(data.productCode || decodeDocIdToProductCode(d.id)),
            targetOnHand: Number.isFinite(Number(data.targetOnHand)) ? Number(data.targetOnHand) : 0,
          };
        });
        setTargets(rows.filter((x) => x.productCode));
        setLoadingTargets(false);
      },
      (err) => {
        setError(err?.message || String(err));
        setLoadingTargets(false);
      }
    );

    return () => unsub();
  }, []);

  // 3) Join prep: fetch cyl_info + cylinders_warehouse for ALL relevant product codes
  // Relevant codes = union( targets codes , stock_sales_live codes )
  useEffect(() => {
    let cancelled = false;

    async function runJoin() {
      setError("");
      setLoadingJoin(true);

      try {
        const targetCodes = targets.map((t) => t.productCode).filter(Boolean);
        const navCodes = stockRows
          .map((r) => (r?.SerialNumber ? String(r.SerialNumber).trim() : ""))
          .filter(Boolean);

        const allCodes = Array.from(new Set([...targetCodes, ...navCodes]));
        if (allCodes.length === 0) {
          if (!cancelled) {
            setCylInfoByProductCode(new Map());
            setWarehouseDocsByProductId(new Map());
            setWarehouseExactByKey(new Map());
            setLoadingJoin(false);
          }
          return;
        }

        // A) cyl_info lookup by productCode (chunked)
        const cylInfoMap = new Map();
        for (const chunk of chunkArray(allCodes, IN_CHUNK)) {
          const qi = query(collection(db, "cyl_info"), where("productCode", "in", chunk));
          const snap = await getDocs(qi);
          snap.forEach((docSnap) => {
            const data = docSnap.data() || {};
            const pc = data.productCode ? String(data.productCode).trim() : "";
            if (pc) cylInfoMap.set(pc, { docId: docSnap.id, ...data });
          });
        }

        // B) warehouse docs by ProductId (docId of cyl_info)
        const productIds = Array.from(
          new Set(Array.from(cylInfoMap.values()).map((v) => String(v.docId)).filter(Boolean))
        );

        const whByPid = new Map();
        const whByKey = new Map();

        if (productIds.length > 0) {
          for (const chunk of chunkArray(productIds, IN_CHUNK)) {
            const qw = query(collection(db, "cylinders_warehouse"), where("ProductId", "in", chunk));
            const snapW = await getDocs(qw);
            snapW.forEach((docSnap) => {
              const data = docSnap.data() || {};
              const pid = data.ProductId ? String(data.ProductId) : "";
              const ser = data.SerialNumber ? String(data.SerialNumber) : "";
              if (!pid) return;

              const full = { id: docSnap.id, ...data };

              if (!whByPid.has(pid)) whByPid.set(pid, []);
              whByPid.get(pid).push(full);

              if (ser) {
                whByKey.set(`${pid}__${ser}`, full);
              }
            });
          }
        }

        if (!cancelled) {
          setCylInfoByProductCode(cylInfoMap);
          setWarehouseDocsByProductId(whByPid);
          setWarehouseExactByKey(whByKey);
          setLoadingJoin(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setLoadingJoin(false);
        }
      }
    }

    runJoin();
    return () => {
      cancelled = true;
    };
  }, [targets, stockRows]);

  useEffect(() => {
    let cancelled = false;

    async function loadCylInfoCodes() {
        setLoadingCylList(true);
        try {
        // NOTE: this reads up to 5000. If cyl_info is bigger, tell me and I’ll paginate.
        const snap = await getDocs(query(collection(db, "cyl_info"), orderBy("productCode", "asc"), limit(5000)));
        const codes = [];
        const mapUpperToReal = new Map(); // "TSMI150/HP" -> "TSMI150/HP" (real)

        snap.forEach((d) => {
        const pc = d.data()?.productCode;
        const real = normaliseCode(pc);
        if (!real) return;

        codes.push(real);

        const upper = real.toUpperCase();
        if (!mapUpperToReal.has(upper)) mapUpperToReal.set(upper, real);
        });

        // Save both: list for display + map for validation
        const unique = Array.from(new Set(codes)).filter(Boolean);

        if (!cancelled) {
        setAllCylProductCodes(unique);
        // store the map on window? no — put it in state below (next block)
        setCylUpperToReal(mapUpperToReal);
        }
        } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
        } finally {
        if (!cancelled) setLoadingCylList(false);
        }
    }

    loadCylInfoCodes();
    return () => {
        cancelled = true;
    };
    }, []);

  // 4) Compute per-code counts
  const reportRows = useMemo(() => {
    const q = (search || "").trim().toLowerCase();

    // Group NAV stock rows by product code
    const navByCode = new Map(); // code -> { totalNav, matchedBookedIn }
    for (const r of stockRows) {
      const code = r?.SerialNumber ? String(r.SerialNumber).trim() : "";
      if (!code) continue;

      const docNo = r?.DocumentNo ? String(r.DocumentNo).trim() : "";
      const navSerial = stripSO(docNo);

      if (!navByCode.has(code)) navByCode.set(code, { totalNav: 0, matchedBookedIn: 0 });
      const bucket = navByCode.get(code);

      bucket.totalNav += 1;

      // check if this exact NAV line has a warehouse match
      const cylInfo = cylInfoByProductCode.get(code);
      const productId = cylInfo?.docId ? String(cylInfo.docId) : null;

      if (productId && navSerial) {
        const key = `${productId}__${navSerial}`;
        if (warehouseExactByKey.has(key)) {
          bucket.matchedBookedIn += 1;
        }
      }
    }

    // Union of codes to display = targets list first (so you can manage) + any nav codes not in targets
    const targetCodeSet = new Set(targets.map((t) => t.productCode));
    const allCodesRaw = Array.from(
    new Set([...targets.map((t) => t.productCode), ...Array.from(navByCode.keys())])
    ).filter(Boolean);

    // ✅ OMIT anything that doesn't exist in cyl_info
    const allCodes = allCodesRaw.filter((code) => cylInfoByProductCode.has(code));

    const rows = allCodes.map((code) => {
      const t = targets.find((x) => x.productCode === code);
      const targetOnHand = t ? Number(t.targetOnHand) : 0;

      const cylInfo = cylInfoByProductCode.get(code) || null;
      const productId = cylInfo?.docId ? String(cylInfo.docId) : null;

      const whDocs = productId ? warehouseDocsByProductId.get(productId) || [] : [];
      const totalInWarehouse = whDocs.filter(isPhysicallyInWarehouse).length;
      const available = whDocs.filter(isAvailableWarehouse).length;
      const allocated = whDocs.filter(isAllocatedWarehouse).length;

      const navCounts = navByCode.get(code) || { totalNav: 0, matchedBookedIn: 0 };
      const openOnNav = navCounts.totalNav;

      // This is your real “in production / not booked in” number
      // (i.e., appears on NAV live stock account but not in warehouse yet)
      const inProduction = Math.max(0, openOnNav - navCounts.matchedBookedIn);

      // realistic view
      const projected = available + inProduction;

      // what you ACTUALLY need right now
      const shortageNow = Math.max(0, targetOnHand - available);

      // what you might need if production lands
      const needToOrder = Math.max(0, targetOnHand - projected);

      let status = { kind: "ok", label: "OK" };
      if (!t && openOnNav > 0) status = { kind: "warn", label: "No target set" };
      if (t && needToOrder > 0) status = { kind: "bad", label: `Order ${needToOrder}` };
      if (t && targetOnHand > 0 && projected < targetOnHand) status = { kind: "bad", label: `Below min` };

      const hay = [
        code,
        productId || "",
        cylInfo?.productDescription || "",
        t ? "target" : "",
      ]
        .join(" ")
        .toLowerCase();

      const passesSearch = !q || hay.includes(q);
      const passesNeeds = !showNeedsOrderOnly || needToOrder > 0;
      const passes = passesSearch && passesNeeds;

      return {
        code,
        hasTarget: targetCodeSet.has(code),
        targetOnHand,
        productId,

        totalInWarehouse,
        available,
        allocated,

        openOnNav,
        inProduction,

        projected,
        needToOrder,

        status,
        passes,
      };
    });

    return rows
      .filter((r) => r.passes)
      .sort((a, b) => {
        // sort: needToOrder desc, then code
        if (b.needToOrder !== a.needToOrder) return b.needToOrder - a.needToOrder;
        return a.code.localeCompare(b.code);
      });
  }, [
    search,
    showNeedsOrderOnly,
    stockRows,
    targets,
    cylInfoByProductCode,
    warehouseDocsByProductId,
    warehouseExactByKey,
  ]);

      const activeSoLines = useMemo(() => {
        if (!activeCode) return [];

        const cylInfo = cylInfoByProductCode.get(activeCode);
        const productId = cylInfo?.docId ? String(cylInfo.docId) : null;

        const lines = (stockRows || [])
            .filter((r) => String(r?.SerialNumber || "").trim() === activeCode)
            .map((r) => {
            const so = r?.DocumentNo ? String(r.DocumentNo).trim() : "";
            const navSerial = stripSO(so);

            const bookedIn =
                productId && navSerial
                ? warehouseExactByKey.has(`${productId}__${navSerial}`)
                : false;

            return {
                id: r.id,
                so,
                navSerial,
                program: r?.Program || null,
                lastNavSync: r?.LastNavSyncUtc,
                bookedIn,
            };
            })
            // newest first by LastNavSyncUtc if available
            .sort((a, b) => {
            const ad = a.lastNavSync?.toDate?.()?.getTime?.() || 0;
            const bd = b.lastNavSync?.toDate?.()?.getTime?.() || 0;
            return bd - ad;
            });

        return lines;
        }, [activeCode, stockRows, cylInfoByProductCode, warehouseExactByKey]);

      // 🔴 Warehouse cylinders NOT found in NAV (your missing ones)
      const activeWarehouseMismatch = useMemo(() => {
        if (!activeCode) return [];

        const cylInfo = cylInfoByProductCode.get(activeCode);
        const productId = cylInfo?.docId ? String(cylInfo.docId) : null;

        if (!productId) return [];

        const whDocs = warehouseDocsByProductId.get(productId) || [];

        // Only care about actual stock in warehouse
        const physical = whDocs.filter(isPhysicallyInWarehouse);

        const navSerials = new Set(
          (activeSoLines || []).map((l) => String(l.navSerial || ""))
        );

        // Anything not in NAV = mismatch
        const missing = physical.filter((w) => {
          const serial = String(w.SerialNumber || "");
          return !navSerials.has(serial);
        });

        return missing;
      }, [activeCode, activeSoLines, warehouseDocsByProductId, cylInfoByProductCode]);

const activeTarget = useMemo(() => {
  if (!activeCode) return null;
  const t = targets.find((x) => x.productCode === activeCode);
  return t ? Number(t.targetOnHand) : null;
}, [activeCode, targets]);

  const stats = useMemo(() => {
    const totalTargets = targets.length;
    const needsOrder = reportRows.filter((r) => r.needToOrder > 0).length;
    return { totalTargets, needsOrder };
  }, [targets, reportRows]);

    async function saveActiveTarget() {
    if (!activeCode) return;

    const qty = Number(activeTargetInput);
    if (!Number.isFinite(qty) || qty < 0) {
        setError("Target must be a number >= 0");
        return;
    }

    setError("");
    setSaving(true);
    try {
    await setDoc(
    doc(db, "stock_min_levels", docIdForProductCode(activeCode)),
        {
            productCode: normaliseCode(activeCode),
            targetOnHand: qty,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
        );
    } catch (e) {
        setError(e?.message || String(e));
    } finally {
        setSaving(false);
    }
    }

  async function saveTarget(productCode, targetOnHand) {
    const code = normaliseCode(productCode);
    const qty = Number(targetOnHand);

    if (!code) {
      setError("Missing product code");
      return;
    }
    if (!Number.isFinite(qty) || qty < 0) {
      setError("Target must be a number >= 0");
      return;
    }

    setError("");
    setSaving(true);
    try {
      // doc id = productCode (simple)
    await setDoc(
    doc(db, "stock_min_levels", docIdForProductCode(activeCode)),
    {
        productCode: normaliseCode(activeCode),
        targetOnHand: qty,
        updatedAt: serverTimestamp(),
    },
    { merge: true }
    );
      setNewCode("");
      setNewTarget("5");
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Stock min levels</h1>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Pill kind={loadingTargets ? "warn" : "ok"}>
              {loadingTargets ? "Loading targets…" : `Targets: ${stats.totalTargets}`}
            </Pill>
            <Pill kind={loadingStock ? "warn" : "ok"}>
              {loadingStock ? "Loading NAV…" : `NAV rows: ${stockRows.length}`}
            </Pill>
            <Pill kind={loadingJoin ? "warn" : "ok"}>{loadingJoin ? "Linking…" : "Linked"}</Pill>
            <Pill kind={stats.needsOrder > 0 ? "bad" : "ok"}>Needs order: {stats.needsOrder}</Pill>
          </div>
        </div>

        <div className="w-full max-w-lg">
          <label className="text-xs font-medium text-slate-600">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="product code / ProductId…"
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
          />

          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => setShowNeedsOrderOnly((v) => !v)}
              className={[
                "rounded-lg border px-3 py-2 text-sm font-medium",
                showNeedsOrderOnly
                  ? "border-rose-200 bg-rose-50 text-rose-700"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              {showNeedsOrderOnly ? "Showing: Needs order only" : "Filter: Needs order only"}
            </button>
          </div>

          {error ? (
            <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          ) : null}
        </div>
      </div>

      {/* Quick add / update */}
      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <label className="text-xs font-medium text-slate-600">Product code</label>
            <select
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
            >
            <option value="">
                {loadingCylList ? "Loading product codes…" : "Select a product code…"}
            </option>
            {allCylProductCodes.map((pc) => (
                <option key={pc} value={pc}>
                {pc}
                </option>
            ))}
            </select>
          </div>

          <div className="w-40">
            <label className="text-xs font-medium text-slate-600">Min on hand</label>
            <input
              value={newTarget}
              onChange={(e) => setNewTarget(e.target.value)}
              inputMode="numeric"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
            />
          </div>

          <button
            disabled={saving}
            onClick={() => saveTarget(newCode, newTarget)}
            className={[
              "rounded-lg px-4 py-2 text-sm font-semibold",
              saving ? "bg-slate-200 text-slate-500" : "bg-slate-900 text-white hover:bg-slate-800",
            ].join(" ")}
          >
            {saving ? "Saving…" : "Save target"}
          </button>
        </div>

        <div className="mt-2 text-xs text-slate-500">
          Stored in <span className="font-mono">stock_min_levels</span> with doc id = product code.
        </div>
      </div>

      {/* Report table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Product code</th>
                <th className="px-4 py-3 font-medium">Target</th>

                <th className="px-4 py-3 font-medium">Available</th>
                <th className="px-4 py-3 font-medium">Allocated</th>
                <th className="px-4 py-3 font-medium">Total</th>

                <th className="px-4 py-3 font-medium">In production</th>
                <th className="px-4 py-3 font-medium">Projected</th>

                <th className="px-4 py-3 font-medium">To order</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {reportRows.map((r) => (
                <tr
                  key={r.code}
                  className="cursor-pointer hover:bg-slate-50/60"
                  onClick={() => {
                    setActiveCode(r.code);
                    const t = targets.find((x) => x.productCode === r.code);
                    setActiveTargetInput(t ? String(t.targetOnHand) : "");
                  }}
                >
                  <td className="px-4 py-3">
                    <Pill kind={r.status.kind}>{r.status.label}</Pill>
                  </td>

                  <td className="px-4 py-3 font-mono">{r.code}</td>

                  <td className="px-4 py-3">
                    {r.hasTarget ? (
                      <Pill kind="ok">{r.targetOnHand}</Pill>
                    ) : (
                      <Pill kind="warn">-</Pill>
                    )}
                  </td>

                  {/* AVAILABLE */}
                  <td className="px-4 py-3">
                    <Pill kind="ok">{r.available}</Pill>
                  </td>

                  {/* ALLOCATED */}
                  <td className="px-4 py-3">
                    <Pill kind={r.allocated > 0 ? "warn" : "default"}>
                      {r.allocated}
                    </Pill>
                  </td>

                  {/* TOTAL */}
                  <td className="px-4 py-3">
                    <Pill kind="default">{r.totalInWarehouse}</Pill>
                  </td>

                  {/* IN PRODUCTION */}
                  <td className="px-4 py-3">
                    <Pill kind={r.inProduction > 0 ? "warn" : "default"}>
                      {r.inProduction}
                    </Pill>
                  </td>

                  {/* PROJECTED */}
                  <td className="px-4 py-3">
                    <Pill kind="ok">{r.projected}</Pill>
                  </td>

                  {/* TO ORDER (THE IMPORTANT ONE) */}
                  <td className="px-4 py-3">
                    {r.needToOrder > 0 ? (
                      <Pill kind="bad">{r.needToOrder}</Pill>
                    ) : (
                      <Pill kind="ok">0</Pill>
                    )}
                  </td>
                </tr>
              ))}

              {reportRows.length === 0 && !(loadingTargets || loadingStock || loadingJoin) ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-slate-500">
                    No rows (check filters/search).
                  </td>
                </tr>
              ) : null}

              {loadingTargets || loadingStock || loadingJoin ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 text-xs text-slate-500">
        Logic:{" "}
        <span className="font-mono">Available = usable stock</span> •{" "}
        <span className="font-mono">Allocated = already spoken for</span> •{" "}
        <span className="font-mono">Projected = available + in production</span> •{" "}
        <span className="font-mono">To order = what you actually need</span>
      </div>
      {activeCode ? (
  <div className="fixed inset-0 z-50">
    {/* backdrop */}
    <div
      className="absolute inset-0 bg-black/40"
      onClick={() => setActiveCode(null)}
    />

    {/* panel */}
    <div className="absolute right-0 top-0 h-full w-full max-w-3xl bg-white shadow-xl">
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-4">
        <div className="min-w-0">
        <div className="text-xs text-slate-500">SO lines for</div>
        <div className="text-lg font-semibold font-mono">{activeCode}</div>

        <div className="mt-2 flex flex-wrap items-end gap-2">
            <div>
            <div className="text-xs font-medium text-slate-600">Target</div>
            <input
                value={activeTargetInput}
                onChange={(e) => setActiveTargetInput(e.target.value)}
                inputMode="numeric"
                placeholder={activeTarget === null ? "Set target…" : String(activeTarget)}
                className="mt-1 w-32 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
            />
            </div>

            <button
            disabled={saving}
            onClick={saveActiveTarget}
            className={[
                "rounded-lg px-3 py-2 text-sm font-semibold",
                saving ? "bg-slate-200 text-slate-500" : "bg-slate-900 text-white hover:bg-slate-800",
            ].join(" ")}
            >
            {saving ? "Saving…" : "Save"}
            </button>

            <div className="text-sm text-slate-600">
            Rows: {activeSoLines.length}
            </div>
        </div>
        </div>
        <button
          onClick={() => setActiveCode(null)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Close
        </button>
      </div>

      <div className="h-[calc(100%-64px)] overflow-auto p-4">
        {/* 🚨 Mismatch warning */}
          {activeWarehouseMismatch.length > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <div className="font-semibold mb-1">⚠️ Stock mismatch detected</div>
              <div>
                {activeWarehouseMismatch.length} cylinder(s) exist in the warehouse but do not appear in NAV.
                These may have already been invoiced and not booked out correctly.
              </div>
            </div>
          )}
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 font-medium">Booked in</th>
                <th className="px-4 py-3 font-medium">SO DocNo</th>
                <th className="px-4 py-3 font-medium">Serial (SO stripped)</th>
                <th className="px-4 py-3 font-medium">Program</th>
                <th className="px-4 py-3 font-medium">Last NAV sync</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {activeSoLines.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    {l.bookedIn ? <Pill kind="ok">Yes</Pill> : <Pill kind="warn">No</Pill>}
                  </td>
                  <td className="px-4 py-3 font-mono">{l.so || "-"}</td>
                  <td className="px-4 py-3 font-mono">{l.navSerial || "-"}</td>
                  <td className="px-4 py-3">{l.program || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{formatTs(l.lastNavSync)}</td>
                </tr>
              ))}

              {activeSoLines.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                    No SO lines found for this product code.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>
          {/* 🚨 Warehouse cylinders NOT in NAV */}
{activeWarehouseMismatch.length > 0 && (
  <div className="mt-6">
    <div className="text-sm font-semibold mb-2 text-amber-700">
      Invoiced from NAV but still in Warehouse ({activeWarehouseMismatch.length})
    </div>

    <div className="overflow-hidden rounded-xl border border-amber-200">
      <table className="w-full text-left text-sm">
        <thead className="bg-amber-50 text-amber-700">
          <tr>
            <th className="px-3 py-2">Serial</th>
            <th className="px-3 py-2">Allocated</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-amber-100">
          {activeWarehouseMismatch.map((w) => (
            <tr key={w.id}>
              <td className="px-3 py-2 font-mono">{w.SerialNumber}</td>

              <td className="px-3 py-2">
                {w.Allocated ? (
                  <Pill kind="warn">Yes</Pill>
                ) : (
                  <Pill kind="ok">No</Pill>
                )}
              </td>

              <td className="px-3 py-2">
                {w.Allocated ? (
                  <Pill kind="warn">Allocated</Pill>
                ) : (
                  <Pill kind="default">Unallocated</Pill>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}
        </div>
      </div>
    </div>

) : null}
    </div>
  );
}